/*
  SaveDocument.jsx
  ----------------
  Interface pour créer une attestation d'un ou plusieurs documents.
*/
import { useEffect, useState } from 'react';
import { Box, Text, Button, Stack, Group, Accordion, ActionIcon, Tooltip, Loader, Center, MultiSelect, Grid, Paper, Card, Divider, Title, Select } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';
import { IconFile, IconUpload, IconX, IconTrash, IconFileText, IconFileSpreadsheet, IconFileTypePdf } from '@tabler/icons-react';
import axios from 'axios';
import { get } from '../tools/Request';
import { useUser } from '../tools/Context';
import { useLocation, useNavigate } from 'react-router-dom';

const hostaddress = process.env.REACT_APP_API_URL;

// 10MB max per file
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export default function SaveDocument() {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [uid, setUid] = useState('');
  const [linkedIds, setLinkedIds] = useState([]);
  const [linkAvailable, setLinkAvailable] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const { refreshPage } = useUser();
  const user = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      refreshPage(false);
      try {
        const [link, folderList] = await Promise.all([get('link', undefined, localStorage.getItem('userToken')), get('folders', undefined, localStorage.getItem('userToken'))]);
        setLinkAvailable(link);
        setFolders(folderList);
      } catch {
        setLinkAvailable([]);
        setFolders([]);
      }
    };
    fetchData();
  }, [user, refreshPage]);

  useEffect(() => {
    if (location.state?.selectedFolderId) {
      setSelectedFolder(String(location.state.selectedFolderId));
    }
  }, [location.state]);

  const handleSubmitFields = async () => {
    if (files.length === 0) {
      showNotification({ title: 'Erreur', message: 'Aucun document fourni', color: 'red' });
      return;
    }

    const tooLarge = files.find((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (tooLarge) {
      showNotification({
        title: 'Fichier trop volumineux',
        message: `“${tooLarge.name}” dépasse 10 MB. Taille max : 10 MB par fichier.`,
        color: 'red'
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      formData.append('linkedIds', linkedIds);
      formData.append('folderId', selectedFolder || '');
      formData.append('token', localStorage.getItem('userToken'));

      const response = await axios.post(`${hostaddress}api/document/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('userToken')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      showNotification({
        title: 'Succès',
        message: 'Document sauvegardé avec succès',
        color: 'green'
      });

      setUid(response.data.uid);
      setFiles([]);
      setLinkedIds([]);
      setSelectedFolder(null);
    } catch (error) {
      showNotification({
        title: 'Une erreur est survenue',
        message: error.response?.data?.message || error.message,
        color: 'red',
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (acceptedFiles) => {
    const tooLarge = acceptedFiles.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (tooLarge.length) {
      showNotification({
        title: 'Fichier trop volumineux',
        message: `Taille max : 10 MB par fichier. (${tooLarge.map((f) => f.name).join(', ')})`,
        color: 'red'
      });
    }
    setFiles((prev) => [...prev, ...acceptedFiles.filter((f) => f.size <= MAX_FILE_SIZE_BYTES)]);
  };

  const handleReject = (rejections) => {
    const tooLarge = rejections
      .filter((r) => r.file?.size > MAX_FILE_SIZE_BYTES)
      .map((r) => r.file?.name)
      .filter(Boolean);

    showNotification({
      title: 'Fichier refusé',
      message: tooLarge.length ? `Taille max : 10 MB par fichier. Refusé : ${tooLarge.join(', ')}` : 'Certains fichiers ont été refusés.',
      color: 'red'
    });
  };

  const handleDeleteFile = (fileName) => {
    setFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const handleCopyUid = () => {
    navigator.clipboard.writeText(uid).then(() => {
      showNotification({ title: 'UID copié', message: 'Identifiant copié avec succès', color: 'green' });
    });
  };

  const redirectProofPage = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate(`/proof?uid=${uid}`);
    }, 500);
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file);
      return <img src={previewUrl} alt={file.name} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />;
    }
    if (file.type === 'application/pdf') return <IconFileTypePdf size={20} color="red" />;
    if (file.name.match(/\.(doc|docx|txt)$/)) return <IconFileText size={20} color="blue" />;
    if (file.name.match(/\.(xls|xlsx|csv)$/)) return <IconFileSpreadsheet size={20} color="green" />;
    return <IconFile size={20} color="gray" />;
  };

  return (
    <Center mt="xl">
      <Card withBorder shadow="sm" radius="md" p="lg" maw={1000} w="100%">
        <Stack spacing="lg">
          <Title order={3}>Sauvegarder des documents</Title>

          <Grid align="stretch" style={{ height: files.length > 10 ? 360 : 'auto' }}>
            <Grid.Col span={6}>
              <Box h="100%">
                <Dropzone
                  onDrop={handleDrop}
                  onReject={handleReject}
                  maxSize={MAX_FILE_SIZE_BYTES}
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                  <Group justify="center" gap="sm" mih={100} style={{ pointerEvents: 'none' }}>
                    <Dropzone.Accept>
                      <IconUpload size={40} color="var(--mantine-color-blue-6)" stroke={1.5} />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <IconX size={40} color="var(--mantine-color-red-6)" stroke={1.5} />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <IconFile size={40} color="var(--mantine-color-dimmed)" stroke={1.5} />
                    </Dropzone.Idle>

                    <Stack gap={2} align="center">
                      <Text size="sm" c="dimmed" ta="center">
                        Déposez vos fichiers ici ou cliquez pour les sélectionner
                      </Text>
                      {/* ✅ size info */}
                      <Text size="xs" c="dimmed" ta="center">
                        Taille maximale : 10 MB par fichier
                      </Text>
                    </Stack>
                  </Group>
                </Dropzone>
              </Box>
            </Grid.Col>

            <Grid.Col span={6}>
              <Box
                h="100%"
                style={{
                  overflowY: 'scroll',
                  overflowX: 'hidden',
                  maxHeight: 360,
                  boxSizing: 'border-box',
                  paddingRight: 4
                }}>
                <Accordion variant="contained" radius="sm">
                  {files.map((file) => (
                    <Accordion.Item key={file.name} value={file.name}>
                      <Accordion.Control icon={getFileIcon(file)}>{file.name}</Accordion.Control>
                      <Accordion.Panel>
                        <Group justify="space-between" align="center">
                          <Text size="sm">{file.name}</Text>
                          <ActionIcon color="red" onClick={() => handleDeleteFile(file.name)}>
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Group>

                        {file.type.startsWith('image/') && (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            style={{
                              marginTop: '0.5rem',
                              maxWidth: '100%',
                              maxHeight: '150px',
                              borderRadius: '4px'
                            }}
                          />
                        )}
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Box>
            </Grid.Col>
          </Grid>

          <Divider />

          <MultiSelect
            label="Lier à d'autres documents ou données"
            placeholder="Sélectionner des identifiants"
            data={linkAvailable.map((link) => ({ value: link.id, label: link.id }))}
            value={linkedIds}
            onChange={setLinkedIds}
            clearable
            searchable
            w="50%"
          />

          <Select
            label="Sélectionner un dossier"
            placeholder="Aucun dossier sélectionné"
            data={folders.map((f) => ({ value: String(f.id), label: f.full_path }))}
            value={selectedFolder}
            onChange={setSelectedFolder}
            searchable
            clearable
            nothingFoundMessage="Aucun dossier disponible"
            w="50%"
          />

          <Group justify="flex-start">
            <Button color="green" onClick={handleSubmitFields} loading={loading} disabled={loading}>
              Enregistrer
            </Button>

            {uid && (
              <Button color="teal" onClick={redirectProofPage} disabled={loading}>
                {loading ? <Loader size="xs" /> : 'Voir la preuve'}
              </Button>
            )}
          </Group>

          {uid && (
            <Paper shadow="xs" p="md" withBorder radius="sm" w="fit-content">
              <Group spacing="xs">
                <Text size="sm">Identifiant d'insertion :</Text>
                <Tooltip label="Copier l'identifiant" position="bottom" withArrow>
                  <Text size="sm" color="teal" onClick={handleCopyUid} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                    {uid}
                  </Text>
                </Tooltip>
              </Group>
            </Paper>
          )}
        </Stack>
      </Card>
    </Center>
  );
}
